// =====================================================================
// src/lib/contexto.js — o que a tela decide a partir de meu_contexto()
//
// Funções PURAS: entrada por parâmetro, saída por retorno, nada de DOM,
// nada de rede. É o que permite testá-las em node sem navegador
// (scripts/teste_tela.js) e mutá-las.
//
// A regra que rege tudo aqui: isto é DESENHO. Quem autoriza é o RLS. Se a
// tela desenhar uma aba a mais, o banco devolve vazio; se desenhar a menos,
// o usuário reclama. Nenhum dos dois é falha de segurança — e por isso a
// tela pode ser simples.
// =====================================================================

// Os estados da tela (0072: + o Termo de Adesão). Não há outro: "carregando" é transitório e
// não decide nada.
export const ESTADOS = Object.freeze({
  ENTRAR:        'entrar',         // sem sessão → formulário
  DEFINIR_SENHA: 'definir_senha',  // chegou pelo link do e-mail (convite/redefinição): senha antes de tudo
  SEM_ACESSO:    'sem_acesso',     // sessão válida, meu_contexto() nulo
  TERMO:         'termo',          // 0072: a imobiliária que ainda deve o aceite do Termo de Adesão (0101: o dono não tem login)
  APP:           'app',            // sessão válida, contexto presente
});

// Sessão presente + contexto presente = app. Sessão presente + contexto
// nulo = a pessoa tem login mas ninguém a cadastrou (ou está inativa):
// diz isso em vez de mostrar uma tela vazia que parece bug. Desde 0012,
// meu_contexto() também pode vir como {bloqueado: motivo} — cadastro ativo,
// mas o hotel (ou a imobiliária) está suspenso: é "sem acesso" na
// mesma tela, com outro texto (motivoSemAcesso).
// `senhaPendente`: a sessão veio de um link de convite/redefinição e a
// senha ainda não foi definida — nada se desenha antes disso.
export function estadoDaTela(sessao, contexto, senhaPendente = false) {
  if (!sessao) return ESTADOS.ENTRAR;
  if (senhaPendente) return ESTADOS.DEFINIR_SENHA;
  // 0072: o único bloqueio que a própria pessoa destrava — aceitando o Termo
  if (contexto && contexto.bloqueado === 'termo_pendente') return ESTADOS.TERMO;
  if (!contexto || contexto.bloqueado) return ESTADOS.SEM_ACESSO;
  return ESTADOS.APP;
}

// O título e o texto da tela de definir senha, pelo tipo do link.
export function textoDefinirSenha(tipo) {
  if (tipo === 'invite') return { titulo: 'Bem-vindo! Defina a sua senha', texto: 'Você foi convidado. Escolha uma senha para entrar no sistema.' };
  if (tipo === 'recovery') return { titulo: 'Senha nova', texto: 'Escolha uma senha nova para o seu acesso.' };
  return { titulo: 'Defina a sua senha', texto: 'Escolha uma senha para o seu acesso.' };
}

// O texto da tela "sem acesso". Só três situações existem; qualquer motivo
// que o banco invente no futuro cai no genérico em vez de quebrar a tela.
export function motivoSemAcesso(contexto) {
  const m = contexto && contexto.bloqueado;
  if (m === 'cliente_inativo') return {
    titulo: 'Acesso suspenso',
    texto: 'O acesso do seu hotel está suspenso. Fale com a administração do sistema.',
  };
  if (m === 'imobiliaria_inativa') return {
    titulo: 'Acesso suspenso',
    texto: 'O acesso da sua imobiliária está suspenso. Fale com quem administra o seu acesso.',
  };
  // 0101: 'proprietario_inativo' (0076) saiu com o login de dono — o
  // proprietário não tem acesso ao sistema (Hugo, 02/11/2026). Se um banco
  // velho ainda o mandar, cai no genérico abaixo, como qualquer motivo novo.
  return {
    titulo: 'Sem acesso',
    texto: 'Seu login existe, mas não está cadastrado em nenhum cliente — ou o cadastro está inativo.',
  };
}

// `garvey.smartgestaohoteleira.com.br` → 'garvey'. Qualquer outra forma
// (localhost, IP, domínio raiz, site do Netlify) → null: sem subdomínio de
// cliente. É CONVENIÊNCIA DE ENDEREÇO, como diz o comentário de
// clientes.slug em 0001 — nunca decide quem é o cliente.
const DOMINIO = 'smartgestaohoteleira.com.br';
export function slugDoHost(hostname) {
  if (!hostname) return null;
  const h = String(hostname).toLowerCase();
  if (!h.endsWith('.' + DOMINIO)) return null;
  const antes = h.slice(0, -(DOMINIO.length + 1));
  if (!antes || antes.includes('.')) return null;   // só um nível: a.b.dominio não é cliente
  if (antes === 'www' || antes === 'app') return null;
  return antes;
}

// Abas na ordem em que a tela desenha: pela `ordem` do catálogo, depois
// pela chave. meu_contexto() já devolve só as alcançáveis; aqui só se
// ordena e se protege contra contexto nulo.
//
// Módulos cuja TELA é da imobiliária (0101): `flats` (os flats dela e o
// cadastro dos donos) e `vaga_garagem`, que até a 0100 era só uma chave
// ("Pedir vaga" nos Detalhes da Reserva, `MODULOS_SEM_TELA`) e passou a
// ser a aba Garagem dela — as vagas e as placas da imobiliária. Só viram
// aba para quem ENTRA como imobiliária: o Administrador do Sistema, que
// recebe o catálogo inteiro, teria um segundo "Garagem" (a do hotel ele já
// tem) e uma aba Flats sem imobiliária a quem perguntar
// (`proprietarios_da_imobiliaria` é da imobiliária logada).
export const MODULOS_DA_IMOBILIARIA = Object.freeze(['flats', 'vaga_garagem']);

export function abas(contexto) {
  const lista = (contexto && Array.isArray(contexto.modulos)) ? contexto.modulos : [];
  const imobiliaria = !!(contexto && contexto.imobiliaria && contexto.imobiliaria.id);
  return lista
    .filter(m => m && m.chave && (m.nivel === 'ver' || m.nivel === 'editar') && (imobiliaria || !MODULOS_DA_IMOBILIARIA.includes(m.chave)))
    .slice()
    .sort((a, b) => (a.ordem - b.ordem) || String(a.chave).localeCompare(String(b.chave)));
}

// Quem é a pessoa, para o cabeçalho. Usuário de sistema não tem cliente;
// gente de imobiliária mostra a imobiliária.
export function tituloDoContexto(contexto) {
  if (!contexto || !contexto.usuario) return '';
  const u = contexto.usuario;
  if (u.sistema) return `${u.nome} · Administrador do Sistema`;
  const partes = [u.nome];
  if (contexto.imobiliaria && contexto.imobiliaria.nome) partes.push(contexto.imobiliaria.nome);
  if (contexto.cliente && contexto.cliente.nome) partes.push(contexto.cliente.nome);
  return partes.join(' · ');
}

// O endereço diz "garvey" mas o token diz "aurora": a pessoa abriu o link
// de outro cliente. Não é invasão (o RLS não deixaria ver nada do Garvey);
// é confusão — avisa. Sem subdomínio, ou usuário de sistema (que não tem
// cliente), não há o que comparar.
export function avisoDeSubdominio(slugHost, contexto) {
  if (!slugHost || !contexto || !contexto.cliente || !contexto.cliente.slug) return null;
  if (contexto.cliente.slug === slugHost) return null;
  return `Este endereço é de "${slugHost}", mas o seu acesso é de "${contexto.cliente.slug}". Você vê só os dados do seu cliente.`;
}

// O módulo que o hash pede: `#painel` e `#painel?tv=1` são o mesmo módulo
// (o que vem depois de `?` é estado da tela, não rota).
export function moduloDoHash(hash) {
  return String(hash || '').replace(/^#/, '').split('?')[0];
}

// ── o dia do hotel (0020) ─────────────────────────────────────────────
// O banco decide o "hoje" (app.hoje); a tela só o repete: fuso e virada
// vêm no contexto do cliente, e daqui sai a data que as telas usam como
// "hoje" — a chegada das 00:30 ainda é de hoje. Sem cliente (o Sistema),
// ou sem Intl no aparelho, vale o dia civil local.
const dataLocal = ms => { const d = new Date(ms); const p = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
export function hojeDoHotel(agoraMs, fuso, virada) {
  try {
    const f = new Intl.DateTimeFormat('en-CA', { timeZone: fuso, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    const p = Object.fromEntries(f.formatToParts(new Date(agoraMs)).map(x => [x.type, x.value]));
    const [vh, vm] = String(virada || '00:00').split(':').map(Number);
    const h = Number(p.hour) % 24, m = Number(p.minute);
    let d = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day));
    if (h < vh || (h === vh && m < (vm || 0))) d -= 86_400_000;
    return new Date(d).toISOString().slice(0, 10);
  } catch (_) { return dataLocal(agoraMs); }
}
export function hojeDe(contexto, agoraMs) {
  const c = contexto && contexto.cliente;
  return c && c.fuso ? hojeDoHotel(agoraMs, c.fuso, c.virada_dia) : dataLocal(agoraMs);
}

// ── A LEGENDA DEBAIXO DO TÍTULO ──────────────────────────────────────
// "Somente leitura" vinha do NÍVEL do módulo e era mentira na guarita: o
// porteiro não está "lendo" nada — está consultando uma placa e decidindo
// um portão. Decisão do Hugo (20/09, "4 A"): cada módulo declara a própria
// legenda por visão, e o nível só fala quando o módulo não disse nada.
export function legendaPadrao(nivel) {
  return nivel === 'editar' ? 'Você pode ver e editar' : 'Somente leitura';
}
export function legendaDoModulo(mod, contexto, legendas) {
  if (!mod) return '';
  const propria = legendas && legendas[mod.chave];
  const texto = typeof propria === 'function' ? propria(contexto) : null;
  return texto || legendaPadrao(mod.nivel);
}
